// =============================================================================
// team-colors-data — static lookup of team colors keyed by Sportradar URN.
// =============================================================================
// 3 hex colors per team. Convention:
//   - Country (flag): three distinct flag colors in flag-stripe order
//     (e.g. USA → [red, white, blue]). Slot 2 is the secondary or
//     "white" position.
//   - Club (kit): primary kit color in slots 1 and 3; secondary / trim
//     in slot 2 (Man City → [lightblue, white, lightblue]).
//
// This file is the static seed used by the SVG hero component until the
// engine schema extension lands (cms.team_assets.color_1 / color_2 /
// color_3 with operator-editable values via admin UI). Until then, this
// is the source of truth for the visual fallback. Operator changes a
// color → edit this file → render bundle rebuild.
//
// Keys are Sportradar competitor URNs (`sr:competitor:NNNN`) — the same
// IDs the engine emits on team objects in match-detail bundles and
// lobby card payloads. Each entry is also exported by lower-cased name
// in NAME_FALLBACK below for the rare case where the URN is missing
// upstream but the name resolves.
// =============================================================================

export type TeamColorTriple = readonly [string, string, string];

/** Sportradar URN → 3-tuple of hex colors. */
export const TEAM_COLORS_BY_URN: Readonly<Record<string, TeamColorTriple>> = Object.freeze({
  // ───────────────────────────────────────────────────────────────────────
  // WC2026 — 48 nations, colors derived from each national flag.
  // ───────────────────────────────────────────────────────────────────────
  // Group A
  'sr:competitor:4744': ['#006847', '#FFFFFF', '#CE1126'], // Czechia (Czech Republic)
  'sr:competitor:4781': ['#006847', '#FFFFFF', '#CE1126'], // Mexico
  'sr:competitor:4736': ['#007749', '#FFB81C', '#001489'], // South Africa
  'sr:competitor:4683': ['#0F47AF', '#C4122E', '#FFFFFF'], // Korea Republic / South Korea

  // Group B
  'sr:competitor:4699': ['#FF0000', '#FFFFFF', '#FF0000'], // Switzerland
  'sr:competitor:4737': ['#FF0000', '#FFFFFF', '#000000'], // Canada
  'sr:competitor:4717': ['#002F6C', '#FFD100', '#FFFFFF'], // Bosnia and Herzegovina
  'sr:competitor:4792': ['#8A1538', '#FFFFFF', '#8A1538'], // Qatar

  // Group C
  'sr:competitor:4753': ['#0033A0', '#FFFFFF', '#D00027'], // Scotland (blue+white)
  'sr:competitor:4748': ['#FEDF00', '#009C3B', '#002776'], // Brazil
  'sr:competitor:4708': ['#C1272D', '#006233', '#000000'], // Morocco
  'sr:competitor:4791': ['#00209F', '#FFFFFF', '#D21034'], // Haiti

  // Group D
  'sr:competitor:4710': ['#E30A17', '#FFFFFF', '#E30A17'], // Turkiye / Turkey
  'sr:competitor:4801': ['#D52B1E', '#FFFFFF', '#0038A8'], // Paraguay
  'sr:competitor:4724': ['#B22234', '#FFFFFF', '#3C3B6E'], // USA
  'sr:competitor:4733': ['#012169', '#FFCD00', '#FFCD00'], // Australia

  // Group E
  'sr:competitor:4695': ['#000000', '#DD0000', '#FFCE00'], // Germany
  'sr:competitor:4747': ['#FFD100', '#0072CE', '#D52B1E'], // Ecuador
  'sr:competitor:4805': ['#009E60', '#FFFFFF', '#FF6600'], // Ivory Coast / Côte d'Ivoire
  'sr:competitor:4830': ['#002B7F', '#FCD116', '#FFFFFF'], // Curaçao

  // Group F
  'sr:competitor:4719': ['#006AA7', '#FECC00', '#FECC00'], // Sweden
  'sr:competitor:4711': ['#AE1C28', '#FFFFFF', '#21468B'], // Netherlands
  'sr:competitor:4754': ['#E70013', '#FFFFFF', '#E70013'], // Tunisia
  'sr:competitor:4723': ['#BC002D', '#FFFFFF', '#BC002D'], // Japan

  // Group G
  'sr:competitor:4750': ['#000000', '#FFD700', '#FF0000'], // Belgium
  'sr:competitor:4709': ['#CE1126', '#FFFFFF', '#000000'], // Egypt
  'sr:competitor:4794': ['#000000', '#FFFFFF', '#000000'], // New Zealand
  'sr:competitor:4751': ['#239F40', '#FFFFFF', '#DA0000'], // Iran / IR Iran

  // Group H
  'sr:competitor:4698': ['#AA151B', '#F1BF00', '#AA151B'], // Spain
  'sr:competitor:4798': ['#0038A8', '#FFFFFF', '#FCD116'], // Uruguay
  'sr:competitor:4799': ['#006C35', '#FFFFFF', '#006C35'], // Saudi Arabia
  'sr:competitor:4860': ['#003893', '#FFFFFF', '#CF142B'], // Cape Verde

  // Group I
  'sr:competitor:4720': ['#EF2B2D', '#002868', '#FFFFFF'], // Norway
  'sr:competitor:4830x': ['#007A3D', '#FFFFFF', '#CE1126'], // Iraq (placeholder URN — verify)
  'sr:competitor:4696': ['#002395', '#FFFFFF', '#ED2939'], // France
  'sr:competitor:4810': ['#00853F', '#FCD116', '#E31B23'], // Senegal

  // Group J
  'sr:competitor:4714': ['#ED2939', '#FFFFFF', '#ED2939'], // Austria
  'sr:competitor:4697': ['#74ACDF', '#FFFFFF', '#74ACDF'], // Argentina
  'sr:competitor:4806': ['#006633', '#FFFFFF', '#D21034'], // Algeria
  'sr:competitor:4815': ['#007A3D', '#FFFFFF', '#CE1126'], // Jordan

  // Group K
  'sr:competitor:4717p': ['#006600', '#FF0000', '#FFFFFF'], // Portugal (placeholder URN)
  'sr:competitor:4793': ['#FCD116', '#003893', '#CE1126'], // Colombia
  'sr:competitor:4818': ['#007FFF', '#FFFFFF', '#007FFF'], // Congo DR / DR Congo
  'sr:competitor:4862': ['#0099B5', '#FFFFFF', '#1EB53A'], // Uzbekistan

  // Group L
  'sr:competitor:4705': ['#FFFFFF', '#CE1124', '#FFFFFF'], // England
  'sr:competitor:4712': ['#FF0000', '#FFFFFF', '#0000FF'], // Croatia (red+white+blue)
  'sr:competitor:4816': ['#006B3F', '#FCD116', '#CE1126'], // Ghana
  'sr:competitor:4811': ['#005AA7', '#FFFFFF', '#D21034'], // Panama

  // ───────────────────────────────────────────────────────────────────────
  // Allsvenskan (Swedish top flight) — kit colors. Slot 2 acts as the
  // trim/secondary. Slots 1 and 3 are the primary (often same hue).
  // ───────────────────────────────────────────────────────────────────────
  // Note: URNs below are placeholders for clubs we know are seeded.
  // Replace with actual cms.team_assets URN values when verified.
  // The pattern still works — these club entries enhance specific
  // matches; unknown teams fall back to hash colors.
  // ───────────────────────────────────────────────────────────────────────

  // (Club-color seeds intentionally left light — engine schema follow-up
  //  will replace this whole file with DB-backed lookup.)
});

/** Lower-cased team name → 3-tuple, fallback when URN missing upstream. */
export const TEAM_COLORS_BY_NAME: Readonly<Record<string, TeamColorTriple>> = Object.freeze({
  // Mirror of the URN map keyed by canonical English name so an emit
  // path that ships `team.name` but no `team.id` still resolves real
  // colors. Synonyms (Korea Republic + South Korea) both point at the
  // same triple so the synonym handling in odds-merge doesn't break
  // color lookups.
  usa: ['#B22234', '#FFFFFF', '#3C3B6E'],
  'united states': ['#B22234', '#FFFFFF', '#3C3B6E'],
  switzerland: ['#FF0000', '#FFFFFF', '#FF0000'],
  qatar: ['#8A1538', '#FFFFFF', '#8A1538'],
  mexico: ['#006847', '#FFFFFF', '#CE1126'],
  'south africa': ['#007749', '#FFB81C', '#001489'],
  paraguay: ['#D52B1E', '#FFFFFF', '#0038A8'],
  brazil: ['#FEDF00', '#009C3B', '#002776'],
  'korea republic': ['#0F47AF', '#C4122E', '#FFFFFF'],
  'south korea': ['#0F47AF', '#C4122E', '#FFFFFF'],
  czechia: ['#006847', '#FFFFFF', '#CE1126'],
  'czech republic': ['#006847', '#FFFFFF', '#CE1126'],
  canada: ['#FF0000', '#FFFFFF', '#000000'],
  'bosnia and herzegovina': ['#002F6C', '#FFD100', '#FFFFFF'],
  'bosnia & herzegovina': ['#002F6C', '#FFD100', '#FFFFFF'],
  scotland: ['#0033A0', '#FFFFFF', '#D00027'],
  morocco: ['#C1272D', '#006233', '#000000'],
  haiti: ['#00209F', '#FFFFFF', '#D21034'],
  turkiye: ['#E30A17', '#FFFFFF', '#E30A17'],
  turkey: ['#E30A17', '#FFFFFF', '#E30A17'],
  australia: ['#012169', '#FFCD00', '#FFCD00'],
  germany: ['#000000', '#DD0000', '#FFCE00'],
  ecuador: ['#FFD100', '#0072CE', '#D52B1E'],
  'ivory coast': ['#009E60', '#FFFFFF', '#FF6600'],
  curacao: ['#002B7F', '#FCD116', '#FFFFFF'],
  sweden: ['#006AA7', '#FECC00', '#FECC00'],
  netherlands: ['#AE1C28', '#FFFFFF', '#21468B'],
  tunisia: ['#E70013', '#FFFFFF', '#E70013'],
  japan: ['#BC002D', '#FFFFFF', '#BC002D'],
  belgium: ['#000000', '#FFD700', '#FF0000'],
  egypt: ['#CE1126', '#FFFFFF', '#000000'],
  'new zealand': ['#000000', '#FFFFFF', '#000000'],
  iran: ['#239F40', '#FFFFFF', '#DA0000'],
  'ir iran': ['#239F40', '#FFFFFF', '#DA0000'],
  spain: ['#AA151B', '#F1BF00', '#AA151B'],
  uruguay: ['#0038A8', '#FFFFFF', '#FCD116'],
  'saudi arabia': ['#006C35', '#FFFFFF', '#006C35'],
  'cape verde': ['#003893', '#FFFFFF', '#CF142B'],
  'cape verde islands': ['#003893', '#FFFFFF', '#CF142B'],
  norway: ['#EF2B2D', '#002868', '#FFFFFF'],
  iraq: ['#007A3D', '#FFFFFF', '#CE1126'],
  france: ['#002395', '#FFFFFF', '#ED2939'],
  senegal: ['#00853F', '#FCD116', '#E31B23'],
  austria: ['#ED2939', '#FFFFFF', '#ED2939'],
  argentina: ['#74ACDF', '#FFFFFF', '#74ACDF'],
  algeria: ['#006633', '#FFFFFF', '#D21034'],
  jordan: ['#007A3D', '#FFFFFF', '#CE1126'],
  portugal: ['#006600', '#FF0000', '#FFFFFF'],
  colombia: ['#FCD116', '#003893', '#CE1126'],
  'congo dr': ['#007FFF', '#FFFFFF', '#1EB53A'],
  'dr congo': ['#007FFF', '#FFFFFF', '#1EB53A'],
  uzbekistan: ['#0099B5', '#FFFFFF', '#1EB53A'],
  england: ['#FFFFFF', '#CE1124', '#FFFFFF'],
  croatia: ['#FF0000', '#FFFFFF', '#0000FF'],
  ghana: ['#006B3F', '#FCD116', '#CE1126'],
  panama: ['#005AA7', '#FFFFFF', '#D21034'],
});

/**
 * Resolve a team's color triple from URN (preferred) or name. Returns
 * `null` when the team isn't in the static seed — caller falls back
 * to hash-derived colors. Name lookup is case-insensitive and tolerant
 * of leading "ir " prefixes that Sportradar ships on some countries.
 */

/* ── AUTO-GENERATED from cms.team_assets crest images (2026-07-10) ──
   Dominant saturated hues per operator-uploaded badge — deterministic
   extraction (no hand-curated club colours; the odds-merge rule about
   club lists concerns NAME MATCHING, not URN-keyed colour data). These
   are the LAST fallback tier: explicit seeds above and engine-supplied
   colours always win. Covers every league whose crests are seeded —
   fixes the washed-out TeamColorSvgHero posters (live critique). */
const CREST_DERIVED_BY_URN: Readonly<Record<string, TeamColorTriple>> = Object.freeze({
  'sr:competitor:1031053': ['#AF353D', '#F0D9DA', '#FFFFFF'], // AVS Futebol SAD
  'sr:competitor:1281': ['#0F3D76', '#E4D604', '#FFFFFF'], // Broendby IF
  'sr:competitor:1284': ['#3D569A', '#ABB6D3', '#FFFFFF'], // FC Copenhagen
  'sr:competitor:1285': ['#1096D4', '#91CFEB', '#FFFFFF'], // Silkeborg IF
  'sr:competitor:1286': ['#025493', '#4C87B3', '#FFFFFF'], // Odense Boldklub
  'sr:competitor:1288': ['#125E4A', '#4A8475', '#FFFFFF'], // Viborg FF
  'sr:competitor:1289': ['#E10612', '#ED878D', '#FFFFFF'], // FC Midtjylland
  'sr:competitor:1291': ['#DD2C1F', '#06649C', '#FFFFFF'], // AGF Aarhus
  'sr:competitor:1292': ['#FEEA12', '#E41C23', '#FFFFFF'], // FC Nordsjaelland
  'sr:competitor:1295': ['#243964', '#B5C7D8', '#FFFFFF'], // Soenderjyske
  'sr:competitor:1301': ['#112654', '#112654', '#FFFFFF'], // Randers FC
  'sr:competitor:1302': ['#B10109', '#E29CA1', '#FFFFFF'], // FC Fredericia
  'sr:competitor:14': ['#E00000', '#E00000', '#FFFFFF'], // Nottingham Forest
  'sr:competitor:1641': ['#0965AA', '#A1C4DE', '#FFFFFF'], // Olympique Marseille
  'sr:competitor:1643': ['#23206A', '#E02419', '#FFFFFF'], // Lille OSC
  'sr:competitor:1644': ['#064371', '#B4C7D5', '#FFFFFF'], // Paris Saint-Germain
  'sr:competitor:1646': ['#004DA1', '#477FBC', '#FFFFFF'], // AJ Auxerre
  'sr:competitor:1647': ['#FADB01', '#00A35B', '#FFFFFF'], // FC Nantes
  'sr:competitor:1649': ['#0B1FA8', '#F4114C', '#FFFFFF'], // Olympique Lyon
  'sr:competitor:1651': ['#730F13', '#730F13', '#FFFFFF'], // FC Metz
  'sr:competitor:1653': ['#C49541', '#FF0B36', '#FFFFFF'], // AS Monaco
  'sr:competitor:1656': ['#E47C19', '#76481F', '#FFFFFF'], // FC Lorient
  'sr:competitor:1658': ['#DF382D', '#F4B216', '#FFFFFF'], // Stade Rennais FC
  'sr:competitor:1661': ['#A68E4F', '#E42129', '#FFFFFF'], // OGC Nice
  'sr:competitor:1662': ['#1A3360', '#80BFE6', '#FFFFFF'], // Le Havre AC
  'sr:competitor:1681': ['#36254D', '#E0184C', '#FFFFFF'], // Toulouse FC
  'sr:competitor:1684': ['#DCC3A2', '#BF9868', '#FFFFFF'], // Angers SCO
  'sr:competitor:17': ['#184175', '#87BAE2', '#FFFFFF'], // Manchester City
  'sr:competitor:1715': ['#E62028', '#F08286', '#FFFFFF'], // Stade Brest 29
  'sr:competitor:1758': ['#DEB608', '#01A549', '#FFFFFF'], // Hammarby IF
  'sr:competitor:1759': ['#F1D30C', '#E33328', '#FFFFFF'], // Djurgårdens IF
  'sr:competitor:1760': ['#B39555', '#F6E702', '#FFFFFF'], // BK Häcken
  'sr:competitor:1761': ['#0A64A6', '#E9B714', '#FFFFFF'], // IFK Göteborg
  'sr:competitor:1762': ['#7D6F0A', '#DBC311', '#FFFFFF'], // IF Elfsborg
  'sr:competitor:1764': ['#063353', '#7A7E25', '#FFFFFF'], // AIK
  'sr:competitor:1766': ['#C4A552', '#202C60', '#FFFFFF'], // Örgryte IS
  'sr:competitor:1767': ['#0458A0', '#E4CD9B', '#FFFFFF'], // Halmstads BK
  'sr:competitor:1775': ['#61AC8D', '#9ECCBA', '#FFFFFF'], // Västerås SK
  'sr:competitor:1783': ['#6F5E15', '#E2BC04', '#FFFFFF'], // Mjällby AIF
  'sr:competitor:1786': ['#ED273D', '#E8CF16', '#FFFFFF'], // GAIS
  'sr:competitor:1787': ['#A7874C', '#554429', '#FFFFFF'], // IF Brommapojkarna
  'sr:competitor:1793': ['#2563AF', '#96ABC5', '#FFFFFF'], // IK Sirius
  'sr:competitor:1807': ['#F7130D', '#E2DAA3', '#FFFFFF'], // Degerfors IF
  'sr:competitor:1891': ['#C3964C', '#D01C35', '#FFFFFF'], // Kalmar FF
  'sr:competitor:1892': ['#3299DB', '#90C9ED', '#FFFFFF'], // Malmö FF
  'sr:competitor:24264': ['#D11833', '#E994A0', '#FFFFFF'], // Girona FC
  'sr:competitor:2524': ['#0A633D', '#509075', '#FFFFFF'], // VFL Wolfsburg
  'sr:competitor:2526': ['#E41520', '#F28C92', '#FFFFFF'], // FC St. Pauli
  'sr:competitor:2534': ['#188D4F', '#67B48B', '#FFFFFF'], // Werder Bremen
  'sr:competitor:2547': ['#EB1D2A', '#EED310', '#FFFFFF'], // Union Berlin
  'sr:competitor:2556': ['#AC0A05', '#E4AEAC', '#FFFFFF'], // FSV Mainz
  'sr:competitor:2569': ['#2368B8', '#A0BEE2', '#FFFFFF'], // TSG Hoffenheim
  'sr:competitor:25777': ['#E1D82C', '#98B5CE', '#FFFFFF'], // FC Arouca
  'sr:competitor:2600': ['#BB3A36', '#CD9F2B', '#FFFFFF'], // FC Augsburg
  'sr:competitor:2671': ['#E4141F', '#F1959A', '#FFFFFF'], // 1. FC Cologne
  'sr:competitor:2672': ['#ED0F40', '#F87F9A', '#FFFFFF'], // Bayern Munich
  'sr:competitor:2673': ['#F8D600', '#7D6A00', '#FFFFFF'], // Borussia Dortmund
  'sr:competitor:2674': ['#E21219', '#F28A8E', '#FFFFFF'], // Eintracht Frankfurt
  'sr:competitor:2676': ['#205DB3', '#93B1DB', '#FFFFFF'], // Hamburger SV
  'sr:competitor:2677': ['#D71932', '#EC8C99', '#FFFFFF'], // VfB Stuttgart
  'sr:competitor:2681': ['#D62423', '#DFD006', '#FFFFFF'], // Bayer Leverkusen
  'sr:competitor:2685': ['#1A2737', '#9E1E32', '#FFFFFF'], // Bologna FC
  'sr:competitor:2686': ['#0064B0', '#92BEDE', '#FFFFFF'], // Atalanta BC
  'sr:competitor:2689': ['#072B56', '#B99364', '#FFFFFF'], // US Lecce
  'sr:competitor:2690': ['#24338A', '#C5A624', '#FFFFFF'], // Parma Calcio
  'sr:competitor:2692': ['#E1002A', '#6E0C24', '#FFFFFF'], // AC Milan
  'sr:competitor:2693': ['#6E4695', '#DE382A', '#FFFFFF'], // ACF Fiorentina
  'sr:competitor:2695': ['#8B7D36', '#8B7D36', '#FFFFFF'], // Udinese Calcio
  'sr:competitor:2696': ['#871E17', '#E1A119', '#FFFFFF'], // Torino FC
  'sr:competitor:2697': ['#012094', '#A6B2DC', '#FFFFFF'], // Inter Milano
  'sr:competitor:2701': ['#002F6C', '#002F6C', '#FFFFFF'], // Hellas Verona
  'sr:competitor:2702': ['#EBAE05', '#8C0B2B', '#FFFFFF'], // AS Roma
  'sr:competitor:2704': ['#12436C', '#4A708F', '#FFFFFF'], // Como 1907
  'sr:competitor:2713': ['#B22524', '#00253E', '#FFFFFF'], // Genoa CFC
  'sr:competitor:2714': ['#07A8E1', '#88D6F2', '#FFFFFF'], // SSC Napoli
  'sr:competitor:2719': ['#122B49', '#BB2F44', '#FFFFFF'], // Cagliari Calcio
  'sr:competitor:2737': ['#085F90', '#DE0E26', '#FFFFFF'], // Pisa SC
  'sr:competitor:2761': ['#E82028', '#C5944D', '#FFFFFF'], // US Cremonese
  'sr:competitor:2793': ['#146E36', '#146E36', '#FFFFFF'], // Sassuolo Calcio
  'sr:competitor:2814': ['#E01018', '#0987CC', '#FFFFFF'], // Espanyol Barcelona
  'sr:competitor:2817': ['#E3B103', '#8C6F02', '#FFFFFF'], // FC Barcelona
  'sr:competitor:2818': ['#C0B032', '#DF3720', '#FFFFFF'], // Rayo Vallecano
  'sr:competitor:2819': ['#035086', '#F4E46D', '#FFFFFF'], // Villarreal CF
  'sr:competitor:2820': ['#C9A848', '#D60F27', '#FFFFFF'], // CA Osasuna
  'sr:competitor:2821': ['#E7254E', '#95CAF1', '#FFFFFF'], // RC Celta de Vigo
  'sr:competitor:2824': ['#11357E', '#DFA013', '#FFFFFF'], // Real Sociedad San Sebastian
  'sr:competitor:2825': ['#EB2C2A', '#F36766', '#FFFFFF'], // Athletic Bilbao
  'sr:competitor:2826': ['#EB1524', '#E8DE04', '#FFFFFF'], // RCD Mallorca
  'sr:competitor:2828': ['#FCDF1B', '#EC3323', '#FFFFFF'], // Valencia CF
  'sr:competitor:2829': ['#E4B410', '#A19740', '#FFFFFF'], // Real Madrid
  'sr:competitor:2833': ['#D81629', '#C9972F', '#FFFFFF'], // Sevilla FC
  'sr:competitor:2836': ['#282A6F', '#E8161F', '#FFFFFF'], // Atletico Madrid
  'sr:competitor:2846': ['#E6CA83', '#C61C31', '#FFFFFF'], // Elche CF
  'sr:competitor:2849': ['#9C0335', '#034E8B', '#FFFFFF'], // Levante UD
  'sr:competitor:2851': ['#FDCC0C', '#0549A3', '#FFFFFF'], // Real Oviedo
  'sr:competitor:2859': ['#0160A2', '#63B14C', '#FFFFFF'], // Getafe CF
  'sr:competitor:2885': ['#03309F', '#97ABD9', '#FFFFFF'], // Deportivo Alaves
  'sr:competitor:2888': ['#E0CF9E', '#015D95', '#FFFFFF'], // Club Brugge
  'sr:competitor:2900': ['#F9A965', '#522B81', '#FFFFFF'], // RSC Anderlecht
  'sr:competitor:2903': ['#2E6FBD', '#A3C0E2', '#FFFFFF'], // KAA Gent
  'sr:competitor:2947': ['#E7CB25', '#716627', '#FFFFFF'], // NAC Breda
  'sr:competitor:2948': ['#EB1B30', '#6D0F18', '#FFFFFF'], // FC Utrecht
  'sr:competitor:2950': ['#DA0121', '#5D1321', '#FFFFFF'], // AZ Alkmaar
  'sr:competitor:2951': ['#028E59', '#49AE89', '#FFFFFF'], // FC Groningen
  'sr:competitor:2952': ['#D8C49D', '#E62128', '#FFFFFF'], // PSV Eindhoven
  'sr:competitor:2955': ['#E70D23', '#F38893', '#FFFFFF'], // FC Twente Enschede
  'sr:competitor:2957': ['#066649', '#E0C213', '#FFFFFF'], // Fortuna Sittard
  'sr:competitor:2959': ['#FA0E0A', '#B1A172', '#FFFFFF'], // Feyenoord Rotterdam
  'sr:competitor:2960': ['#BA9A39', '#DCC98D', '#FFFFFF'], // Sparta Rotterdam
  'sr:competitor:2962': ['#E33931', '#028B57', '#FFFFFF'], // NEC Nijmegen
  'sr:competitor:2964': ['#024D9E', '#417BB8', '#FFFFFF'], // SC Heerenveen
  'sr:competitor:2967': ['#E0031B', '#F2919C', '#FFFFFF'], // Excelsior Rotterdam
  'sr:competitor:2968': ['#EF6417', '#F9B48D', '#FFFFFF'], // FC Volendam
  'sr:competitor:2971': ['#255FB1', '#6CC2EE', '#FFFFFF'], // PEC Zwolle
  'sr:competitor:2972': ['#F10003', '#0A539A', '#FFFFFF'], // SC Telstar
  'sr:competitor:2977': ['#1CA9CC', '#93D7E7', '#FFFFFF'], // Heracles Almelo
  'sr:competitor:2979': ['#ECCB0B', '#BE103A', '#FFFFFF'], // Go Ahead Eagles
  'sr:competitor:2999': ['#C32C22', '#ACABCF', '#FFFFFF'], // SC Braga
  'sr:competitor:3': ['#F9B714', '#F6DA88', '#FFFFFF'], // Wolverhampton Wanderers
  'sr:competitor:30': ['#034696', '#A8C0DC', '#FFFFFF'], // Brighton & Hove Albion
  'sr:competitor:3001': ['#048863', '#4EAC92', '#FFFFFF'], // Sporting CP
  'sr:competitor:3002': ['#27436F', '#3F75A4', '#FFFFFF'], // FC Porto
  'sr:competitor:3006': ['#CDAD31', '#CE3837', '#FFFFFF'], // SL Benfica
  'sr:competitor:3013': ['#E2B40C', '#7F3E9B', '#FFFFFF'], // Nacional da Madeira
  'sr:competitor:3014': ['#B29A19', '#105D24', '#FFFFFF'], // Moreirense FC
  'sr:competitor:3036': ['#814A00', '#E98600', '#FFFFFF'], // Rio Ave FC
  'sr:competitor:3050': ['#E41622', '#E98288', '#FFFFFF'], // Besiktas Istanbul
  'sr:competitor:3051': ['#5A0D26', '#76B8E6', '#FFFFFF'], // Trabzonspor
  'sr:competitor:3053': ['#CD2224', '#E48A8B', '#FFFFFF'], // Samsunspor
  'sr:competitor:3056': ['#F59195', '#EC2C35', '#FFFFFF'], // Antalyaspor
  'sr:competitor:3061': ['#BA2346', '#F3AB19', '#FFFFFF'], // Galatasaray Istanbul
  'sr:competitor:3064': ['#F19792', '#0E8FD3', '#FFFFFF'], // Caykur Rizespor
  'sr:competitor:3065': ['#0A7144', '#00A2D9', '#FFFFFF'], // Kocaelispor
  'sr:competitor:3072': ['#F6BC02', '#CC0102', '#FFFFFF'], // Kayserispor
  'sr:competitor:3085': ['#AAD5C4', '#52A986', '#FFFFFF'], // Konyaspor
  'sr:competitor:3086': ['#1D4B71', '#3190C2', '#FFFFFF'], // Istanbul Basaksehir
  'sr:competitor:33': ['#122257', '#122257', '#FFFFFF'], // Tottenham Hotspur
  'sr:competitor:34': ['#F4DA1E', '#0560A8', '#FFFFFF'], // Leeds United
  'sr:competitor:35': ['#E35108', '#FBDE00', '#FFFFFF'], // Manchester United
  'sr:competitor:36360': ['#E01B50', '#EF8AA6', '#FFFFFF'], // RB Leipzig
  'sr:competitor:37': ['#7B2B3A', '#35A3D5', '#FFFFFF'], // West Ham United
  'sr:competitor:38': ['#0B1985', '#BA8D29', '#FFFFFF'], // Chelsea FC
  'sr:competitor:38396': ['#11963D', '#FAEC01', '#FFFFFF'], // CD Tondela
  'sr:competitor:39': ['#07A6DA', '#6F582A', '#FFFFFF'], // Newcastle United
  'sr:competitor:40': ['#95BFE5', '#F5E114', '#FFFFFF'], // Aston Villa
  'sr:competitor:41': ['#D72E3D', '#EF7B86', '#FFFFFF'], // Sunderland AFC
  'sr:competitor:42': ['#E5040A', '#A28A55', '#FFFFFF'], // Arsenal FC
  'sr:competitor:43': ['#8F0F0F', '#8F0F0F', '#FFFFFF'], // Fulham FC
  'sr:competitor:44': ['#B84953', '#52AC9E', '#FFFFFF'], // Liverpool FC
  'sr:competitor:4475': ['#BA0D2F', '#04215C', '#FFFFFF'], // Norway
  'sr:competitor:4479': ['#002394', '#F7C704', '#FFFFFF'], // Bosnia and Herzegovina
  'sr:competitor:4481': ['#012654', '#CE1226', '#FFFFFF'], // France
  'sr:competitor:4500': ['#DCC620', '#0B93D3', '#FFFFFF'], // Estoril Praia
  'sr:competitor:4688': ['#005292', '#FBCA01', '#FFFFFF'], // Sweden
  'sr:competitor:4691': ['#006633', '#D41639', '#FFFFFF'], // Algeria
  'sr:competitor:4695': ['#005EB8', '#005EB8', '#FFFFFF'], // Scotland
  'sr:competitor:4698': ['#F6B901', '#AC1519', '#FFFFFF'], // Spain
  'sr:competitor:4699': ['#FF0000', '#FF8181', '#FFFFFF'], // Switzerland
  'sr:competitor:4700': ['#E30A17', '#F3989D', '#FFFFFF'], // Turkiye
  'sr:competitor:4704': ['#FD0100', '#006500', '#FFFFFF'], // Portugal
  'sr:competitor:4705': ['#AE1C28', '#20458A', '#FFFFFF'], // Netherlands
  'sr:competitor:4711': ['#DC0100', '#FECD00', '#FFFFFF'], // Germany
  'sr:competitor:4713': ['#CA1835', '#CA1835', '#FFFFFF'], // England
  'sr:competitor:4714': ['#D7141A', '#10447D', '#FFFFFF'], // Czechia
  'sr:competitor:4715': ['#FF0202', '#161695', '#FFFFFF'], // Croatia
  'sr:competitor:4717': ['#FBD924', '#EF3240', '#FFFFFF'], // Belgium
  'sr:competitor:4718': ['#C8112E', '#C8112E', '#FFFFFF'], // Austria
  'sr:competitor:4723': ['#2F8738', '#2F81F7', '#FFFFFF'], // Uzbekistan
  'sr:competitor:4724': ['#B51F47', '#133866', '#FFFFFF'], // USA
  'sr:competitor:4725': ['#0035A6', '#426BBE', '#FFFFFF'], // Uruguay
  'sr:competitor:4729': ['#E70113', '#F4939B', '#FFFFFF'], // Tunisia
  'sr:competitor:4735': ['#CD2F3B', '#01469F', '#FFFFFF'], // Korea Republic
  'sr:competitor:4736': ['#017648', '#E03C31', '#FFFFFF'], // South Africa
  'sr:competitor:4739': ['#01853F', '#E31A23', '#FFFFFF'], // Senegal
  'sr:competitor:4741': ['#022169', '#ED99AB', '#FFFFFF'], // Australia
  'sr:competitor:4748': ['#009340', '#F7C604', '#FFFFFF'], // Brazil
  'sr:competitor:4752': ['#FF0202', '#FF8888', '#FFFFFF'], // Canada
  'sr:competitor:4753': ['#013893', '#D0272E', '#FFFFFF'], // Cape Verde
  'sr:competitor:4757': ['#FADA02', '#EB1C24', '#FFFFFF'], // Ecuador
  'sr:competitor:4758': ['#CE1226', '#E5D39A', '#FFFFFF'], // Egypt
  'sr:competitor:4764': ['#CE1125', '#006A3F', '#FFFFFF'], // Ghana
  'sr:competitor:4766': ['#DA0202', '#28A144', '#FFFFFF'], // IR Iran
  'sr:competitor:4767': ['#CD1225', '#62AD87', '#FFFFFF'], // Iraq
  'sr:competitor:4768': ['#F77F01', '#019E60', '#FFFFFF'], // Ivory Coast
  'sr:competitor:4770': ['#BB002B', '#E499AA', '#FFFFFF'], // Japan
  'sr:competitor:4771': ['#017A3D', '#CE1126', '#FFFFFF'], // Jordan
  'sr:competitor:4778': ['#C1262D', '#175B32', '#FFFFFF'], // Morocco
  'sr:competitor:4781': ['#016847', '#CE1226', '#FFFFFF'], // Mexico
  'sr:competitor:4784': ['#022169', '#C90F2C', '#FFFFFF'], // New Zealand
  'sr:competitor:4789': ['#D52B1E', '#0037A7', '#FFFFFF'], // Paraguay
  'sr:competitor:4792': ['#891437', '#CF9FAE', '#FFFFFF'], // Qatar
  'sr:competitor:48': ['#03039E', '#4343B7', '#FFFFFF'], // Everton FC
  'sr:competitor:4819': ['#74ACDF', '#E5C8A0', '#FFFFFF'], // Argentina
  'sr:competitor:4820': ['#FECC00', '#033085', '#FFFFFF'], // Colombia
  'sr:competitor:4823': ['#007EFE', '#CD0E21', '#FFFFFF'], // Congo DR
  'sr:competitor:4834': ['#025531', '#4A856C', '#FFFFFF'], // Saudi Arabia
  'sr:competitor:4860': ['#E3CD20', '#0575B2', '#FFFFFF'], // Union Saint-Gilloise
  'sr:competitor:4872': ['#FE1006', '#F68882', '#FFFFFF'], // Vejle BK
  'sr:competitor:49531': ['#D7B00B', '#786E24', '#FFFFFF'], // FC Famalicao
  'sr:competitor:50': ['#C81D1D', '#DD8888', '#FFFFFF'], // Brentford FC
  'sr:competitor:5138': ['#D02833', '#EB766D', '#FFFFFF'], // Gaziantep FK
  'sr:competitor:5164': ['#DA131B', '#082458', '#FFFFFF'], // Panama
  'sr:competitor:55827': ['#012B7E', '#F3E316', '#FFFFFF'], // Curacao
  'sr:competitor:5885': ['#E3071E', '#093F7B', '#FFFFFF'], // 1. FC Heidenheim
  'sr:competitor:6': ['#620945', '#8E4B78', '#FFFFFF'], // Burnley FC
  'sr:competitor:60': ['#C81419', '#E0A4A6', '#FFFFFF'], // AFC Bournemouth
  'sr:competitor:6070': ['#0E1B42', '#078AC4', '#FFFFFF'], // Paris FC
  'sr:competitor:6362': ['#E57B21', '#40A03E', '#FFFFFF'], // Alanyaspor
  'sr:competitor:7': ['#3E7EBC', '#9BBDDD', '#FFFFFF'], // Crystal Palace
  'sr:competitor:7040': ['#ECCB08', '#6A3E9C', '#FFFFFF'], // Eyupspor
  'sr:competitor:7229': ['#D10F34', '#00209E', '#FFFFFF'], // Haiti
  'sr:competitor:744135': ['#F2C466', '#D7281B', '#FFFFFF'], // Estrela Amadora
  'sr:competitor:7802': ['#DF0610', '#EBBBAD', '#FFFFFF'], // Genclerbirligi SK
});

export function lookupTeamColors(
  urnOrName: string | undefined | null,
): TeamColorTriple | null {
  if (!urnOrName) return null;
  if (TEAM_COLORS_BY_URN[urnOrName]) return TEAM_COLORS_BY_URN[urnOrName];
  const key = urnOrName.trim().toLowerCase();
  if (TEAM_COLORS_BY_NAME[key]) return TEAM_COLORS_BY_NAME[key];
  // Crest-derived fallback tier (URN keys only — names are locale-variable).
  if (CREST_DERIVED_BY_URN[urnOrName]) return CREST_DERIVED_BY_URN[urnOrName];
  return null;
}
